import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getSmsMonthlyBilling } from "../services/smsBillingService";
import { useSmsBillingStore } from "../stores/smsBillingStore";
import type {
  MonthRange,
  SmsBillingRow,
  SmsBillingTotals,
} from "../types/smsBilling.types";

const BILLING_KEY = ["smsBilling", "monthly"] as const;

const billingQueryKey = (range: MonthRange) =>
  [...BILLING_KEY, range.fromMonth, range.toMonth] as const;

/**
 * 적용된 범위의 청구 내역. 원본 로그 집계라 무겁다 —
 * 같은 범위는 다시 요청하지 않고(staleTime: Infinity), 재조회는 새로고침 버튼으로만.
 */
export const useSmsMonthlyBilling = () => {
  const appliedRange = useSmsBillingStore((s) => s.appliedRange);
  return useQuery({
    queryKey: appliedRange ? billingQueryKey(appliedRange) : BILLING_KEY,
    queryFn: ({ signal }) => getSmsMonthlyBilling(appliedRange!, signal),
    enabled: appliedRange !== null,
    staleTime: Infinity,
    retry: false,
  });
};

export const useSmsBillingActions = () => {
  const qc = useQueryClient();

  const refresh = useCallback(() => {
    const range = useSmsBillingStore.getState().appliedRange;
    if (!range) return;
    return qc.invalidateQueries({ queryKey: billingQueryKey(range) });
  }, [qc]);

  return { refresh };
};

const EMPTY_TOTALS: SmsBillingTotals = {
  smsCount: 0,
  lmsCount: 0,
  smsAmount: 0,
  lmsAmount: 0,
  totalCount: 0,
  totalAmount: 0,
};

/** 업체명 필터를 적용한 행과 그 합계 */
export const useFilteredBillingRows = (rows: SmsBillingRow[] | undefined) => {
  const keyword = useSmsBillingStore((s) => s.keyword);

  return useMemo(() => {
    const needle = keyword.trim().toLowerCase();
    const filtered = !needle
      ? (rows ?? [])
      : (rows ?? []).filter((r) => r.companyName.toLowerCase().includes(needle));

    const totals = filtered.reduce<SmsBillingTotals>(
      (acc, r) => ({
        smsCount: acc.smsCount + r.smsCount,
        lmsCount: acc.lmsCount + r.lmsCount,
        smsAmount: acc.smsAmount + r.smsAmount,
        lmsAmount: acc.lmsAmount + r.lmsAmount,
        totalCount: acc.totalCount + r.totalCount,
        totalAmount: acc.totalAmount + r.totalAmount,
      }),
      EMPTY_TOTALS,
    );

    return { rows: filtered, totals };
  }, [rows, keyword]);
};
